type WhisperSegment = {
  start: number | null;
  end: number | null;
  text: string;
};

type LyricsPayload = {
  text?: string | null;
  userLyrics?: string | null;
  metadata?: unknown | null;
} | null;

/**
 * Which bad words the lyrics are masked against.
 *
 * SUGGESTED hides only the words Lutely flags as worth masking; ALL hides every
 * bad word on the list, including the ones that otherwise just mark the track as
 * explicit.
 */
export const LYRICS_MASK_MODES = {
  SUGGESTED: "SUGGESTED",
  ALL: "ALL",
} as const;

export type LyricsMaskMode =
  (typeof LYRICS_MASK_MODES)[keyof typeof LYRICS_MASK_MODES];

export type LyricsMask = {
  mode: LyricsMaskMode;
  /** Masked on top of the mode, case insensitively. */
  extraWords: string[];
};

/**
 * The masking the served lyrics were produced with.
 *
 * Read defensively rather than off the generated DTO: the field is served by
 * `GET /track-assets/lyrics`, and until the client is regenerated the type does
 * not carry it. A track that never chose comes back as the suggested words.
 */
export const getLyricsMask = (lyrics?: unknown): LyricsMask => {
  const mask = (lyrics as { mask?: Partial<LyricsMask> } | null | undefined)
    ?.mask;

  return {
    mode:
      mask?.mode === LYRICS_MASK_MODES.ALL
        ? LYRICS_MASK_MODES.ALL
        : LYRICS_MASK_MODES.SUGGESTED,
    extraWords: Array.isArray(mask?.extraWords)
      ? mask.extraWords.filter((word) => typeof word === "string")
      : [],
  };
};

const SEGMENT_GAP_SECONDS = 2;
const MAX_LINE_CHARS = 60;
const MAX_LINE_WORDS = 12;

const normalizeWhitespace = (value: string) =>
  value.replace(/\s+/g, " ").trim();

const sanitizeSegmentText = (value: string) =>
  normalizeWhitespace(value.replace(/\[\*\]/g, ""));

const toNumberOrNull = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const extractSegments = (metadata: unknown): WhisperSegment[] => {
  if (!metadata || typeof metadata !== "object") return [];
  const segments = (metadata as { segments?: unknown }).segments;
  if (!Array.isArray(segments)) return [];

  return segments
    .map((segment) => {
      if (!segment || typeof segment !== "object") return null;
      const segmentText =
        typeof (segment as { text?: unknown }).text === "string"
          ? (segment as { text: string }).text
          : "";
      return {
        start: toNumberOrNull((segment as { start?: unknown }).start),
        end: toNumberOrNull((segment as { end?: unknown }).end),
        text: segmentText,
      };
    })
    .filter((segment): segment is WhisperSegment => segment !== null);
};

const wrapLine = (line: string): string[] => {
  const words = line.split(/\s+/).filter(Boolean);
  if (!words.length) return [];

  const lines: string[] = [];
  let currentWords: string[] = [];
  let currentLength = 0;

  for (const word of words) {
    const nextLength =
      currentLength + (currentWords.length > 0 ? 1 : 0) + word.length;
    const exceedsChars = nextLength > MAX_LINE_CHARS;
    const exceedsWords = currentWords.length >= MAX_LINE_WORDS;

    if (currentWords.length && (exceedsChars || exceedsWords)) {
      lines.push(currentWords.join(" "));
      currentWords = [word];
      currentLength = word.length;
      continue;
    }

    currentWords.push(word);
    currentLength = nextLength;
  }

  if (currentWords.length) {
    lines.push(currentWords.join(" "));
  }

  return lines;
};

/**
 * Split text the user typed into display lines, preserving their structure.
 *
 * No wrapping and no gap heuristics: the blank lines between stanzas are the
 * author's, and a line they chose to write long stays one line. Whitespace-only
 * lines collapse to a true empty line so the rendered block copies back out as
 * an empty line rather than a stray space.
 */
const splitAuthoredLines = (text: string): string[] =>
  text.split(/\r?\n/).map((line) => normalizeWhitespace(line));

const trimBlankEdges = (lines: string[]): string[] => {
  let start = 0;
  let end = lines.length;
  while (start < end && !lines[start]) start += 1;
  while (end > start && !lines[end - 1]) end -= 1;
  return lines.slice(start, end);
};

const splitByNewlines = (text: string): string[] => {
  const rawLines = text.split(/\r?\n/);
  const lines: string[] = [];

  for (const rawLine of rawLines) {
    const trimmed = normalizeWhitespace(rawLine);
    if (!trimmed) {
      lines.push("");
      continue;
    }
    lines.push(...wrapLine(trimmed));
  }

  return lines;
};

const splitByPunctuation = (text: string): string[] => {
  const hasPunctuation = /[.!?]/.test(text);
  if (!hasPunctuation) return [text];
  return text
    .split(/(?<=[.!?])\s+/)
    .map((line) => normalizeWhitespace(line))
    .filter(Boolean);
};

const getMetadataText = (metadata: unknown) => {
  if (!metadata || typeof metadata !== "object") return "";
  const text = (metadata as { text?: unknown }).text;
  return typeof text === "string" ? text : "";
};

export const getLyricsLines = (lyrics?: LyricsPayload): string[] => {
  if (!lyrics) return [];

  // When the user typed the lyrics, their line and stanza breaks ARE the
  // structure. The segment path below infers breaks from gaps in the audio,
  // which scatters blank lines through a verse whenever the singer pauses
  // between lines — right for a machine transcription, wrong for authored text.
  const authored = lyrics.userLyrics;
  if (typeof authored === "string" && authored.trim()) {
    return trimBlankEdges(splitAuthoredLines(authored));
  }

  const segments = extractSegments(lyrics.metadata);
  if (segments.length) {
    const lines: string[] = [];

    segments.forEach((segment, index) => {
      const text = sanitizeSegmentText(segment.text);
      if (text) {
        lines.push(text);
      }

      if (index < segments.length - 1) {
        const nextSegment = segments[index + 1];
        if (
          segment.end !== null &&
          nextSegment.start !== null &&
          nextSegment.start - segment.end > SEGMENT_GAP_SECONDS
        ) {
          lines.push("");
        }
      }
    });

    return lines;
  }

  const rawText =
    typeof lyrics.text === "string"
      ? lyrics.text
      : getMetadataText(lyrics.metadata);
  const normalizedText = normalizeWhitespace(rawText);
  if (!normalizedText) return [];

  if (rawText.includes("\n") || rawText.includes("\r")) {
    return splitByNewlines(rawText);
  }

  const sentenceLines = splitByPunctuation(rawText);
  return sentenceLines.flatMap((line) => wrapLine(line));
};

/**
 * The lyrics as a single block of text, ready for the clipboard.
 *
 * Built from the same lines the viewer renders, so what someone copies is
 * exactly what they see — blank lines included, and without the stray
 * non-breaking spaces a per-line span layout used to leave behind.
 */
export const getLyricsText = (lyrics?: LyricsPayload): string =>
  getLyricsLines(lyrics).join("\n");
